import { useEffect, useState } from "react";
import { TrendingUp, Users, Briefcase, FileText, Eye, Loader2, ArrowUpRight, Globe2 } from "lucide-react";
import { api } from "../../lib/api";

// Mobile-first analytics view. Real numbers from /api/admin/stats.
// Top queries / SEO traffic placeholders are sourced from the in-DB lead data
// (city + subject distribution) until GA4 / Search Console wiring is added.

export default function MobileAnalytics() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats").then((r) => setStats(r.data)),
            Promise.all([
                api.get("/admin/inquiries"),
                api.get("/admin/registrations"),
            ]).then(([a, b]) => setItems([...a.data, ...b.data])),
        ])
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>;
    if (!stats) return null;

    const totalLeads = stats.inquiries.total + stats.registrations.total;
    const conversion = totalLeads === 0 ? 0 :
        Math.round(((stats.inquiries.converted || 0) + (stats.registrations.converted || 0)) / totalLeads * 100);

    // Top cities from leads
    const cityCount = {};
    items.forEach((it) => { if (it.city) cityCount[it.city] = (cityCount[it.city] || 0) + 1; });
    const topCities = Object.entries(cityCount).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Top subject interests (from inquiries `subject` and registrations `subjects`)
    const subjectCount = {};
    items.forEach((it) => {
        const s = it.subject || it.subjects || "";
        if (s) {
            s.split(/[,/]/).map((x) => x.trim()).filter(Boolean).forEach((x) => {
                subjectCount[x] = (subjectCount[x] || 0) + 1;
            });
        }
    });
    const topSubjects = Object.entries(subjectCount).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Last-30-day daily lead trend
    const days = Array.from({ length: 30 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (29 - i));
        return d.toISOString().slice(0, 10);
    });
    const dayCount = Object.fromEntries(days.map((d) => [d, 0]));
    items.forEach((it) => {
        const d = (it.created_at || "").slice(0, 10);
        if (d in dayCount) dayCount[d] += 1;
    });
    const max = Math.max(1, ...Object.values(dayCount));

    return (
        <div data-testid="mobile-analytics" className="px-4 py-4 space-y-4 pb-4">
            {/* Hero metrics */}
            <div className="grid grid-cols-2 gap-3">
                <Stat icon={Users} label="Total Leads (30d)" value={totalLeads} sub={`${stats.inquiries.total} inq · ${stats.registrations.total} demo`} />
                <Stat icon={TrendingUp} label="Conversion %" value={`${conversion}%`} sub="of all leads converted" />
                <Stat icon={Briefcase} label="Tutor Apps" value={stats.tutor_applications.total} sub={`${stats.tutor_applications.new || 0} to review`} />
                <Stat icon={FileText} label="Blog Posts" value={stats.blogs} sub="published & draft" />
            </div>

            {/* 30-day trend chart */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Last 30 Days · Daily Leads</div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div className="flex items-end gap-1 h-24" data-testid="lead-trend-chart">
                    {days.map((d) => {
                        const v = dayCount[d];
                        return (
                            <div
                                key={d}
                                className="flex-1 bg-blue-500/20 rounded-sm relative group"
                                style={{ height: `${Math.max(2, (v / max) * 100)}%` }}
                                title={`${d}: ${v}`}
                            >
                                <div className="absolute inset-0 bg-[#1E3A8A] rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-2">
                    <span>{days[0].slice(5)}</span>
                    <span>{days[14].slice(5)}</span>
                    <span>Today</span>
                </div>
            </div>

            {/* Top cities */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Globe2 className="w-4 h-4 text-[#1E3A8A]" />
                    <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Top Cities (from leads)</div>
                </div>
                {topCities.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No city data yet.</div>
                ) : (
                    <div className="space-y-2">
                        {topCities.map(([city, n]) => (
                            <div key={city} className="flex items-center gap-3 text-sm">
                                <div className="flex-1 truncate font-semibold text-[#0F172A]">{city}</div>
                                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                                    <div className="h-full bg-[#1E3A8A] rounded-full" style={{ width: `${(n / topCities[0][1]) * 100}%` }} />
                                </div>
                                <div className="text-xs font-bold text-slate-600 w-8 text-right">{n}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Top subjects */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Top Subject Interests</div>
                </div>
                {topSubjects.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No subject data yet.</div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {topSubjects.map(([s, n]) => (
                            <span key={s} className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                                {s} <span className="opacity-60 text-[10px]">×{n}</span>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Status pipeline */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Lead Pipeline</div>
                <div className="space-y-2">
                    {[
                        ["new", "New", "bg-amber-500"],
                        ["contacted", "Contacted", "bg-blue-500"],
                        ["in_progress", "In Progress", "bg-indigo-500"],
                        ["converted", "Converted", "bg-emerald-500"],
                        ["lost", "Lost", "bg-slate-400"],
                    ].map(([k, l, color]) => {
                        const n = (stats.inquiries[k] || 0) + (stats.registrations[k] || 0);
                        const pct = totalLeads ? (n / totalLeads) * 100 : 0;
                        return (
                            <div key={k} className="flex items-center gap-3 text-sm">
                                <div className="w-20 text-xs font-semibold text-slate-600">{l}</div>
                                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                                    <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                                </div>
                                <div className="text-xs font-bold text-[#0F172A] w-10 text-right">{n}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* SEO insights notice */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 border border-slate-200 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2 flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" /> Google Search Performance
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                    Connect Google Search Console to see top queries (e.g. "home tutors in Bhopal"), impressions, click-through rates and indexing status here. See <code className="bg-slate-100 px-1 rounded text-[10px]">GO_LIVE_GUIDE.md §4</code> for setup.
                </p>
            </div>
        </div>
    );
}

const Stat = ({ icon: Icon, label, value, sub }) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-3.5">
        <div className="flex items-center justify-between mb-1.5">
            <Icon className="w-4 h-4 text-[#1E3A8A]" />
        </div>
        <div className="font-heading text-2xl font-bold text-[#0F172A] leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-2 font-semibold">{label}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
    </div>
);
